import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useI18n, type Lang } from "../../i18n";
import { useAuth } from "../../auth/AuthProvider";
import type { ApiClient } from "../../api/client";
import {
  createAgent,
  deleteAgent,
  deleteComputer,
  listAgents,
  listComputers,
  restartComputer,
  setComputerBrowser,
  upgradeComputer,
} from "../../api/collab";
import { shouldAutoFocusEditable } from "../../lib/mobileFocus";
import type { AgentProfile, Computer } from "../../types/collab";
import { ConnectComputerPanel } from "./ConnectComputerPanel";
import { Avatar } from "./avatar";
import { isMobileViewport } from "./mobile";

const POLL_MS = 4000;
const UPGRADE_TIMEOUT_MS = 120_000;

type UpgradePhase = "requesting" | "installing" | "reconnecting" | "success" | "failed";

type UpgradeProgress = {
  targetVersion: string;
  phase: UpgradePhase;
};

export function reconcileUpgradeProgress(
  progress: UpgradeProgress,
  computer: Pick<Computer, "online" | "version">
): UpgradeProgress {
  if (computer.version === progress.targetVersion) return { ...progress, phase: "success" };
  if (progress.phase === "success" || progress.phase === "failed") return progress;
  if (!computer.online) return { ...progress, phase: "reconnecting" };
  if (computer.online && progress.phase === "reconnecting") return { ...progress, phase: "installing" };
  return progress;
}

/** The runtimes `sj` probes for on a computer (detect.rs). Used to render the
 * Detected-runtimes catalog (installed vs. not) and the create-agent picker. */
const SUPPORTED_RUNTIMES: { id: string; label: string }[] = [
  { id: "codex", label: "Codex CLI" },
  { id: "claude-code", label: "Claude Code" },
];

const RUNTIME_LABELS: Record<string, string> = Object.fromEntries(
  SUPPORTED_RUNTIMES.map((r) => [r.id, r.label])
);

function runtimeLabel(id: string): string {
  return RUNTIME_LABELS[id] ?? id;
}

/** Friendly default agent names — 百家姓 (小赵…) in Chinese, Alice/Bob in English.
 * The display name uses the locale; the @handle stays ASCII (pinyin/lowercase). */
const AGENT_NAME_POOL: Record<Lang, { name: string; handle: string }[]> = {
  zh: [
    { name: "小赵", handle: "zhao" }, { name: "小钱", handle: "qian" }, { name: "小孙", handle: "sun" },
    { name: "小李", handle: "li" }, { name: "小周", handle: "zhou" }, { name: "小吴", handle: "wu" },
    { name: "小郑", handle: "zheng" }, { name: "小王", handle: "wang" }, { name: "小冯", handle: "feng" },
    { name: "小陈", handle: "chen" }, { name: "小褚", handle: "chu" }, { name: "小卫", handle: "wei" },
  ],
  en: [
    { name: "Alice", handle: "alice" }, { name: "Bob", handle: "bob" }, { name: "Carol", handle: "carol" },
    { name: "Dave", handle: "dave" }, { name: "Erin", handle: "erin" }, { name: "Frank", handle: "frank" },
    { name: "Grace", handle: "grace" }, { name: "Heidi", handle: "heidi" }, { name: "Ivan", handle: "ivan" },
    { name: "Judy", handle: "judy" }, { name: "Mallory", handle: "mallory" }, { name: "Niaj", handle: "niaj" },
  ],
};

/** Pick the first pool entry whose @handle isn't already taken; number-suffix if
 * the whole pool is used. `taken` is the account's existing agent handles. */
function suggestAgent(lang: Lang, taken: string[]): { name: string; handle: string } {
  const pool = AGENT_NAME_POOL[lang] ?? AGENT_NAME_POOL.en;
  const used = new Set(taken.map((h) => h.toLowerCase()));
  const free = pool.find((p) => !used.has(p.handle));
  if (free) return free;
  for (let n = 2; n < 99; n += 1) {
    const cand = pool.find((p) => !used.has(`${p.handle}${n}`));
    if (cand) return { name: `${cand.name}${n}`, handle: `${cand.handle}${n}` };
  }
  return pool[0];
}

/**
 * Computers view: a computer list (column 2) + the selected
 * computer's detail (column 3) with OS / version / runtimes and Restart /
 * Upgrade / Delete actions. Owns its own polling.
 */
export function ComputersView({ baseUrl, accountId, workspaceName }: { baseUrl: string; accountId: string; workspaceName: string }) {
  const { client } = useAuth();
  const { t } = useI18n();
  const [computers, setComputers] = useState<Computer[]>([]);
  const [latestVersion, setLatestVersion] = useState<string | undefined>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedId;

  const refresh = useCallback(async () => {
    if (!accountId || !client) return;
    try {
      const res = await listComputers(client, accountId);
      setComputers(res.computers);
      setLatestVersion(res.latestVersion);
      // Default-select the first computer once — desktop only. On mobile the
      // panes stack, so land on the list (else "back" wouldn't stick).
      if (!selectedRef.current && res.computers.length > 0 && !isMobileViewport()) setSelectedId(res.computers[0].id);
    } catch {
      /* keep last-known list */
    } finally {
      setLoaded(true);
    }
  }, [accountId, client]);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const selected = computers.find((c) => c.id === selectedId) ?? null;

  const detail = connecting || !!selected;
  const back = () => { setConnecting(false); setSelectedId(null); };

  return (
    <div className={`ws-view${detail ? " detail" : ""}`}>
      <aside className="ws-side">
        <div className="ws-side-head"><span className="ws-side-headtitle">{workspaceName}</span></div>
        <div className="ws-side-scroll">
          <div className="ws-side-section">
            <div className="ws-side-section-head">
              <span className="ws-side-label">{t("collab.computers")}</span>
              <button
                type="button"
                className="ws-icon-btn"
                onClick={() => setConnecting((v) => !v)}
                title={t("collab.connect")}
                aria-label={t("collab.connect")}
              >
                {connecting ? "×" : "+"}
              </button>
            </div>
            {loaded && computers.length === 0 ? <p className="ws-side-empty">{t("collab.empty")}</p> : null}
            <ul className="ws-comp-list">
            {computers.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`ws-comp-item${c.id === selectedId ? " active" : ""}`}
                  onClick={() => { setSelectedId(c.id); setConnecting(false); }}
                >
                  <span className={`ws-dot ${c.online ? "on" : "off"}`} aria-hidden />
                  <span className="ws-comp-item-main">
                    <span className="ws-comp-item-name">{c.name}</span>
                    <span className="ws-comp-item-sub">
                      {t(`collab.kind.${c.kind}`)}
                      {c.version ? ` · v${c.version}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            ))}
            </ul>
          </div>
        </div>
      </aside>

      <section className="ws-content">
        {detail ? <button type="button" className="ws-mback" onClick={back}>‹ {t("collab.computers")}</button> : null}
        {connecting ? (
          <div className="ws-detail-scroll">
            <ConnectComputerPanel baseUrl={baseUrl} accountId={accountId} />
          </div>
        ) : selected && client ? (
          <ComputerDetail
            computer={selected}
            latestVersion={latestVersion}
            client={client}
            accountId={accountId}
            onChanged={refresh}
            onDeleted={() => { setSelectedId(null); void refresh(); }}
          />
        ) : (
          <div className="ws-content-empty">{t("collab.computer.selectPrompt")}</div>
        )}
      </section>
    </div>
  );
}

function ComputerDetail({
  computer: c,
  latestVersion,
  client,
  accountId,
  onChanged,
  onDeleted,
}: {
  computer: Computer;
  latestVersion?: string;
  client: ApiClient;
  accountId: string;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState<UpgradeProgress | null>(null);
  const [browserBusy, setBrowserBusy] = useState(false);
  const [browserError, setBrowserError] = useState<string | null>(null);
  const local = c.kind === "local";
  const browser = c.browser ?? { available: false, enabled: false };
  const upgradable = Boolean(local && c.version && latestVersion && c.version !== latestVersion);
  const upgradeActive = upgrade?.phase === "requesting" || upgrade?.phase === "installing" || upgrade?.phase === "reconnecting";

  useEffect(() => {
    setUpgrade(null);
    setBrowserError(null);
  }, [c.id]);

  useEffect(() => {
    setUpgrade((current) => current ? reconcileUpgradeProgress(current, c) : null);
  }, [c.online, c.version]);

  useEffect(() => {
    if (!upgradeActive) return;
    const id = window.setTimeout(() => {
      setUpgrade((current) => current && current.phase !== "success" ? { ...current, phase: "failed" } : current);
    }, UPGRADE_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [upgradeActive, upgrade?.targetVersion]);

  useEffect(() => {
    if (upgrade?.phase !== "success") return;
    const id = window.setTimeout(() => setUpgrade(null), 4000);
    return () => window.clearTimeout(id);
  }, [upgrade?.phase]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, opts?: { confirm?: string; deleted?: boolean }) => {
      if (opts?.confirm && !window.confirm(opts.confirm)) return;
      setBusy(true);
      try {
        await fn();
        if (opts?.deleted) onDeleted();
        else onChanged();
      } catch {
        /* the poll will reconcile */
      } finally {
        setBusy(false);
      }
    },
    [onChanged, onDeleted]
  );

  const requestUpgrade = useCallback(async () => {
    if (!latestVersion || upgradeActive) return;
    const targetVersion = latestVersion;
    setUpgrade({ targetVersion, phase: "requesting" });
    try {
      await upgradeComputer(client, accountId, c.id);
      setUpgrade((current) => current?.targetVersion === targetVersion && current.phase === "requesting"
        ? { ...current, phase: "installing" }
        : current);
      onChanged();
    } catch {
      setUpgrade((current) => current?.targetVersion === targetVersion ? { ...current, phase: "failed" } : current);
    }
  }, [accountId, client, c.id, latestVersion, onChanged, upgradeActive]);

  const updateBrowser = useCallback(async (enabled: boolean) => {
    if (!enabled && !window.confirm(t("collab.computer.browserDisableConfirm"))) return;
    setBrowserBusy(true);
    setBrowserError(null);
    try {
      await setComputerBrowser(client, accountId, c.id, enabled);
      onChanged();
    } catch (error) {
      setBrowserError(error instanceof Error ? error.message : t("collab.computer.browserError"));
    } finally {
      setBrowserBusy(false);
    }
  }, [accountId, c.id, client, onChanged, t]);

  const upgradeLabel = upgrade
    ? t(`collab.action.upgrade.${upgrade.phase}`, { version: upgrade.targetVersion })
    : "";

  return (
    <div className="ws-detail-scroll">
      <div className="ws-detail">
        <div className="ws-detail-hero">
          <span className="ws-detail-avatar" aria-hidden>🖥</span>
          <div>
            <h3 className="ws-detail-name">{c.name}</h3>
            <p className="ws-detail-status">
              <span className={`ws-dot ${c.online ? "on" : "off"}`} aria-hidden />
              {c.online ? t("collab.computer.connected") : t("collab.offline")}
              <span className="ws-detail-host"> · {c.platform}</span>
            </p>
          </div>
        </div>

        <div className="ws-detail-section">
          <div className="ws-detail-label">{t("collab.computer.info")}</div>
          <dl className="ws-detail-grid">
            <dt>{t("collab.computer.os")}</dt>
            <dd className="ws-mono">{c.platform}</dd>
            {c.version ? (
              <>
                <dt>{t("collab.computer.version")}</dt>
                <dd>
                  <span className={`ws-mono ${upgradable ? "ws-ver-upd" : ""}`}>v{c.version}</span>
                  {upgradable ? <span className="ws-ver-hint"> ({t("collab.computer.updateAvailable")})</span> : null}
                </dd>
              </>
            ) : null}
            <dt>{t("collab.computer.runtimes")}</dt>
            <dd>
              <span className="ws-runtimes">
                {SUPPORTED_RUNTIMES.map((r) => {
                  const on = c.runtimes.includes(r.id);
                  return (
                    <span key={r.id} className={`ws-runtime${on ? " on" : " off"}`}>
                      {r.label}
                      {on ? null : <span className="ws-runtime-note"> · {t("collab.runtimes.notInstalled")}</span>}
                    </span>
                  );
                })}
              </span>
            </dd>
          </dl>
        </div>

        {local ? (
          <div className="ws-detail-section">
            <div className="ws-detail-label">{t("collab.computer.browserTitle")}</div>
            <div className="ws-browser-control">
              <div className="ws-browser-copy">
                <strong>
                  {browser.enabled
                    ? t("collab.computer.browserEnabled")
                    : browser.available
                      ? t("collab.computer.browserDisabled")
                      : t("collab.computer.browserUnavailable")}
                </strong>
                <span>{t("collab.computer.browserDesc")}</span>
              </div>
              <label className="ws-browser-switch">
                <input
                  type="checkbox"
                  checked={browser.enabled}
                  disabled={browserBusy || !c.online || !browser.available}
                  onChange={(event) => void updateBrowser(event.target.checked)}
                  aria-label={t("collab.computer.browserTitle")}
                />
                <span aria-hidden><i /></span>
              </label>
            </div>
            {browserBusy ? <div className="ws-browser-message"><span className="spinner inline" /> {t("collab.computer.browserSaving")}</div> : null}
            {browserError ? <div className="ws-browser-message error">{browserError}</div> : null}
          </div>
        ) : null}

        <ComputerAgents client={client} accountId={accountId} computer={c} />

        {local ? (
          <div className="ws-detail-section">
            <div className="ws-detail-label">{t("collab.computer.actions")}</div>

            <div className="ws-action-card">
              <div className="ws-action-head">{t("collab.computer.serviceTitle")}</div>
              <p className="ws-action-desc">
                {upgradable || upgradeActive
                  ? t("collab.computer.serviceUpgradeDesc", { version: upgrade?.targetVersion ?? latestVersion! })
                  : t("collab.computer.serviceDesc")}
              </p>
              <div className="ws-detail-actions">
                <button type="button" className="ws-btn" disabled={busy || upgradeActive || !c.online} onClick={() => act(() => restartComputer(client, accountId, c.id))}>
                  ↺ {t("collab.action.restart")}
                </button>
                {upgradable || upgradeActive ? (
                  <button
                    type="button"
                    className={`ws-btn accent ws-upgrade-button${upgradeActive ? " updating" : ""}`}
                    disabled={busy || upgradeActive || !c.online}
                    onClick={() => void requestUpgrade()}
                  >
                    {upgradeActive ? <span className="spinner inline" aria-hidden /> : null}
                    {upgradeActive ? upgradeLabel : t("collab.action.upgradeTo", { version: latestVersion! })}
                  </button>
                ) : (
                  <button type="button" className="ws-btn" disabled title={t("collab.action.upToDate")}>
                    ✓ {t("collab.action.upToDate")}
                  </button>
                )}
              </div>

              {upgrade ? (
                <div className={`ws-upgrade-status ${upgrade.phase}`} role="status" aria-live="polite">
                  <span className="ws-upgrade-status-icon" aria-hidden>
                    {upgradeActive ? <span className="spinner inline" /> : upgrade.phase === "success" ? "✓" : "!"}
                  </span>
                  <div className="ws-upgrade-status-main">
                    <strong>{upgradeLabel}</strong>
                    <span>{t(`collab.action.upgrade.${upgrade.phase}Detail`)}</span>
                    {upgradeActive ? <span className="ws-upgrade-track" aria-hidden><span /></span> : null}
                  </div>
                </div>
              ) : null}

              <div className="ws-verify">
                <div className="ws-verify-head">
                  <span className="ws-mono ws-verify-prompt">›_</span> {t("collab.computer.verifyTitle")}
                </div>
                <p className="ws-verify-desc">{t("collab.computer.verifyDesc")}</p>
                <CommandRow command="sj status" />
                <CommandRow command="sj restart" />
              </div>
            </div>

            <div className="ws-action-card danger">
              <div className="ws-action-row">
                <div>
                  <div className="ws-action-head">{t("collab.action.delete")}</div>
                  <p className="ws-action-desc">{t("collab.computer.deleteDesc")}</p>
                </div>
                <button type="button" className="ws-btn danger" disabled={busy} onClick={() => act(() => deleteComputer(client, accountId, c.id), { confirm: t("collab.action.deleteConfirm"), deleted: true })}>
                  🗑 {t("collab.action.delete")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A copyable one-line terminal command (the "verify from terminal" rows). */
function CommandRow({ command }: { command: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked — the text is selectable */
    }
  }, [command]);
  return (
    <div className="ws-cmd">
      <code className="ws-cmd-text">{command}</code>
      <button type="button" className="ws-cmd-copy" onClick={copy} title={t("collab.copy")} aria-label={t("collab.copy")}>
        {copied ? "✓" : "⧉"}
      </button>
    </div>
  );
}

function ComputerAgents({ client, accountId, computer }: { client: ApiClient; accountId: string; computer: Computer }) {
  const { t } = useI18n();
  const [agents, setAgents] = useState<AgentProfile[]>([]);
  const [takenHandles, setTakenHandles] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const local = computer.kind === "local";
  const runtimeOptions = computer.runtimes;
  const canCreateAgent = local && computer.online && runtimeOptions.length > 0;
  const unavailableMessage = t(local ? "collab.agent.runtimeUnavailable" : "collab.agent.cloudUnavailable");

  const refresh = useCallback(async () => {
    try {
      const { agents: all } = await listAgents(client, accountId);
      setAgents(all.filter((a) => a.computerId === computer.id));
      setTakenHandles(all.map((a) => a.handle)); // account-wide (@handle is unique per account)
    } catch {
      /* ignore */
    }
  }, [client, accountId, computer.id]);

  useEffect(() => { void refresh(); }, [refresh]);

  const remove = async (id: string) => {
    try { await deleteAgent(client, accountId, id); await refresh(); } catch { /* ignore */ }
  };

  // Distinct directories agents on this computer run in (context isolation).
  const workspaces = Array.from(new Set(agents.map((a) => a.workspace).filter((w): w is string => !!w)));

  return (
    <>
      <div className="ws-detail-section">
        <div className="ws-detail-head">
          <div className="ws-detail-label">
            {t("collab.computer.agents")} <span className="ws-count">{agents.length}</span>
          </div>
          {local ? (
            <button
              type="button"
              className="ws-btn accent small"
              onClick={() => setCreating((v) => !v)}
              disabled={!canCreateAgent}
              title={canCreateAgent ? undefined : unavailableMessage}
            >
              {creating ? t("collab.agent.cancel") : `+ ${t("collab.agent.new")}`}
            </button>
          ) : null}
        </div>

        {local && !canCreateAgent ? <p className="ws-hint">{unavailableMessage}</p> : null}
        {!local ? <p className="ws-hint">{computer.online ? t("collab.agent.cloudFixed") : unavailableMessage}</p> : null}

        {creating && canCreateAgent ? (
          <AgentCreateForm
            runtimeOptions={runtimeOptions}
            local={local}
            busy={busy}
            takenHandles={takenHandles}
            onSubmit={async (input) => {
              setBusy(true);
              try {
                await createAgent(client, accountId, { ...input, computerType: "local_device", computerId: computer.id });
                setCreating(false);
                await refresh();
                return null;
              } catch (err) {
                return String(err).includes("409") ? t("collab.agent.taken") : t("collab.agent.invalid");
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : null}

        {agents.length === 0 && !creating ? <p className="ws-muted">{t("collab.agent.empty")}</p> : null}
        <ul className="ws-agent-list">
          {agents.map((a) => (
            <li key={a.id} className="ws-agent-item">
              <Avatar name={a.displayName} id={a.id} size={34} />
              <div className="ws-agent-main">
                <div className="ws-agent-name">
                  {a.displayName}
                  {a.runtime ? <span className="ws-agent-runtime">{runtimeLabel(a.runtime)}</span> : null}
                </div>
                <div className="ws-agent-sub">
                  <span className="ws-mono ws-agent-h">@{a.handle}</span>
                  {a.workspace ? <span className="ws-mono ws-agent-ws" title={a.workspace}>📁 {a.workspace}</span> : null}
                </div>
                {a.persona ? <div className="ws-agent-persona">{a.persona}</div> : null}
              </div>
              <span className={`ws-agent-status ${computer.online ? "on" : "off"}`}>
                <span className={`ws-dot ${computer.online ? "on" : "off"}`} aria-hidden />
                {computer.online ? t("collab.computer.connected") : t("collab.offline")}
              </span>
              {local ? <button type="button" className="ws-btn ghost small" onClick={() => void remove(a.id)}>{t("collab.action.delete")}</button> : null}
            </li>
          ))}
        </ul>
        <p className="ws-hint">{t("collab.agent.hint")}</p>
      </div>

      {local ? (
        <div className="ws-detail-section">
          <div className="ws-detail-label">📂 {t("collab.computer.workspaces")}</div>
          <p className="ws-action-desc">{t("collab.computer.workspacesDesc")}</p>
          {workspaces.length === 0 ? (
            <p className="ws-muted">{t("collab.computer.workspacesEmpty")}</p>
          ) : (
            <ul className="ws-wslist">
              {workspaces.map((w) => (
                <li key={w} className="ws-wslist-item">
                  <span className="ws-mono">📁 {w}</span>
                  <span className="ws-wslist-agents">
                    {agents.filter((a) => a.workspace === w).map((a) => `@${a.handle}`).join(" ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </>
  );
}

/** The "+ Create" agent form: codename, name, runtime, workspace dir, persona. */
function AgentCreateForm({
  runtimeOptions,
  local,
  busy,
  takenHandles,
  onSubmit,
}: {
  runtimeOptions: string[];
  local: boolean;
  busy: boolean;
  takenHandles: string[];
  onSubmit: (input: { handle: string; displayName: string; runtime: string; workspace?: string; persona?: string }) => Promise<string | null>;
}) {
  const { t, lang } = useI18n();
  // Pre-fill a friendly default (小赵 / Alice…) so the form opens ready-to-go.
  const [suggestion] = useState(() => suggestAgent(lang, takenHandles));
  const [handle, setHandle] = useState(suggestion.handle);
  const [name, setName] = useState(suggestion.name);
  const [runtime, setRuntime] = useState(runtimeOptions[0] ?? "");
  // The workspace directory mirrors the codename by default until the user edits
  // it, so each agent lands in a dir named after its @codename.
  const [workspace, setWorkspace] = useState(local ? suggestion.handle : "");
  const [workspaceEdited, setWorkspaceEdited] = useState(false);
  const [persona, setPersona] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onHandleChange = (v: string) => {
    setHandle(v);
    if (local && !workspaceEdited) setWorkspace(v.trim().replace(/^@/, ""));
  };

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const h = handle.trim().replace(/^@/, "");
      if (h.length < 2) { setError(t("collab.agent.invalid")); return; }
      setError(null);
      const err = await onSubmit({
        handle: h,
        displayName: name.trim() || h,
        runtime,
        workspace: local && workspace.trim() ? workspace.trim() : undefined,
        persona: persona.trim() || undefined,
      });
      if (err) setError(err);
    },
    [handle, name, runtime, workspace, persona, local, onSubmit, t]
  );

  return (
    <form className="ws-agent-new" onSubmit={submit}>
      <div className="ws-field-row">
        <label className="ws-field">
          <span className="ws-field-label">{t("collab.agent.handle")}</span>
          <div className="set-username">
            <span className="set-username-at ws-mono">@</span>
            <input className="ws-input set-username-input" placeholder="cindy" value={handle} onChange={(e) => onHandleChange(e.target.value)} autoFocus={shouldAutoFocusEditable()} autoCapitalize="none" spellCheck={false} maxLength={30} />
          </div>
        </label>
        <label className="ws-field">
          <span className="ws-field-label">{t("collab.agent.name")}</span>
          <input className="ws-input" placeholder="Cindy" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
        </label>
      </div>

      <label className="ws-field">
        <span className="ws-field-label">{t("collab.agent.runtime")}</span>
        <select className="ws-input" value={runtime} onChange={(e) => setRuntime(e.target.value)}>
          {runtimeOptions.map((id) => (
            <option key={id} value={id}>{runtimeLabel(id)}</option>
          ))}
        </select>
      </label>

      {local ? (
        <label className="ws-field">
          <span className="ws-field-label">{t("collab.agent.workspace")}</span>
          <input className="ws-input ws-mono" placeholder={handle.trim().replace(/^@/, "") || "cindy"} value={workspace} onChange={(e) => { setWorkspace(e.target.value); setWorkspaceEdited(true); }} autoCapitalize="none" spellCheck={false} />
          <span className="ws-field-hint">{t("collab.agent.workspaceHint")}</span>
        </label>
      ) : null}

      <label className="ws-field">
        <span className="ws-field-label">{t("collab.agent.persona")}</span>
        <textarea className="ws-input" rows={3} placeholder={t("collab.agent.personaPlaceholder")} value={persona} onChange={(e) => setPersona(e.target.value)} />
      </label>

      <div className="ws-agent-new-actions">
        <button type="submit" className="ws-btn primary small" disabled={busy || !runtime || handle.trim().length < 2}>{t("collab.agent.create")}</button>
      </div>
      {error ? <p className="ws-warn">{error}</p> : null}
    </form>
  );
}
